import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { RequireAuth, RequireEnabled, RequireCap, RequireFlag } from './auth/guards'
import { MeProvider } from './auth/MeContext'
import { EntitlementsProvider } from './entitlements/EntitlementsContext'
import { NavCountsProvider } from './components/NavCounts'
// Public/lightweight routes — eager (must render without Suspense delay)
import PendingActivation from './pages/PendingActivation'
import AccessDenied from './pages/AccessDenied'
import AccountBlocked from './pages/AccountBlocked'
import PublicStatus from './pages/PublicStatus'
// Authenticated pages — lazy-loaded for route-level code splitting
const Dashboard          = lazy(() => import('./pages/Dashboard'))
const AgentList          = lazy(() => import('./pages/AgentList'))
const AgentDetail        = lazy(() => import('./pages/AgentDetail'))
const AgentForm          = lazy(() => import('./pages/AgentForm'))
const CaseList           = lazy(() => import('./pages/CaseList'))
const CaseDetail         = lazy(() => import('./pages/CaseDetail'))
const MaintenanceList    = lazy(() => import('./pages/MaintenanceList'))
const PersonsAdmin       = lazy(() => import('./pages/PersonsAdmin'))
const CustomersAdmin     = lazy(() => import('./pages/CustomersAdmin'))
const ProvidersAdmin     = lazy(() => import('./pages/ProvidersAdmin'))
const UserVmPermissions  = lazy(() => import('./pages/UserVmPermissions'))
const UnknownAgents      = lazy(() => import('./pages/UnknownAgents'))
const SettingsShell      = lazy(() => import('./pages/settings/SettingsShell'))
const RequestChooser     = lazy(() => import('./pages/RequestChooser'))
const RequestForm        = lazy(() => import('./pages/RequestForm'))
const VmRequestForm      = lazy(() => import('./pages/VmRequestForm'))
const MyRequests         = lazy(() => import('./pages/MyRequests'))
const RequestsList       = lazy(() => import('./pages/RequestsList'))
const RequestDetail      = lazy(() => import('./pages/RequestDetail'))
const VmRequestDetail    = lazy(() => import('./pages/VmRequestDetail'))
const PushSettings       = lazy(() => import('./pages/PushSettings'))
const CloudWorkersAdmin  = lazy(() => import('./pages/CloudWorkersAdmin'))
const Hosts              = lazy(() => import('./pages/Hosts'))
const VmList             = lazy(() => import('./pages/VmList'))
const VmDetail           = lazy(() => import('./pages/VmDetail'))
const AuditLog           = lazy(() => import('./pages/AuditLog'))
const AboutVitals        = lazy(() => import('./pages/AboutVitals'))
const UsagePolicy        = lazy(() => import('./pages/UsagePolicy'))
const Privacy            = lazy(() => import('./pages/Privacy'))

function FullPageSpinner() { return <div className="vitals" style={{ padding: 40 }}>Lädt…</div> }

function AuthedApp() {
  return (
    <RequireAuth>
      <RequireEnabled fallback={<FullPageSpinner />}>
        <MeProvider fallback={<FullPageSpinner />}>
          <EntitlementsProvider loadingFallback={<FullPageSpinner />}>
          <NavCountsProvider>
          <Suspense fallback={<FullPageSpinner />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/agents" element={<RequireCap cap="viewAgentList"><AgentList /></RequireCap>} />
              <Route path="/agents/new" element={<RequireCap cap="createAgent"><AgentForm /></RequireCap>} />
              <Route path="/agents/:guid" element={<RequireCap cap="openAgentDetail"><AgentDetail /></RequireCap>} />
              <Route path="/agents/:guid/edit" element={<RequireCap cap="editAgent"><AgentForm /></RequireCap>} />
              <Route path="/cases" element={<RequireCap cap="viewCases"><CaseList /></RequireCap>} />
              <Route path="/cases/:id" element={<RequireCap cap="viewCases"><CaseDetail /></RequireCap>} />
              <Route path="/maintenance" element={<RequireCap cap="manageEntities"><MaintenanceList /></RequireCap>} />
              {/* Person-Merge: EINE Personen-Verwaltung (Lead+) ersetzt /admin/contacts + /admin/users.
                  Die alten Pfade leiten dorthin um (alte Deeplinks bleiben gültig). */}
              <Route path="/admin/persons" element={<RequireCap cap="manageEntities"><PersonsAdmin /></RequireCap>} />
              <Route path="/admin/contacts" element={<Navigate to="/admin/persons" replace />} />
              <Route path="/admin/users" element={<Navigate to="/admin/persons" replace />} />
              <Route path="/admin/customers" element={<RequireCap cap="manageEntities"><CustomersAdmin /></RequireCap>} />
              <Route path="/admin/providers" element={<RequireCap cap="manageEntities"><ProvidersAdmin /></RequireCap>} />
              <Route path="/admin/unknown" element={<RequireCap cap="manageEntities"><UnknownAgents /></RequireCap>} />
              <Route path="/admin/users/:id/vm-permissions" element={<RequireCap cap="manageVmPermissions"><UserVmPermissions /></RequireCap>} />
              <Route path="/admin/settings" element={<RequireCap cap="manageSettings"><SettingsShell /></RequireCap>} />
              <Route path="/admin/cloud-workers" element={<RequireCap cap="manageCloudWorkers"><CloudWorkersAdmin /></RequireCap>} />
              <Route path="/requests/new" element={<RequireCap cap="submitRequest"><RequestChooser /></RequireCap>} />
              <Route path="/requests/new/agent" element={<RequireCap cap="submitRequest"><RequestForm /></RequireCap>} />
              <Route path="/requests/new/vm" element={<RequireCap cap="submitRequest"><VmRequestForm /></RequireCap>} />
              <Route path="/requests/mine" element={<RequireCap cap="submitRequest"><MyRequests /></RequireCap>} />
              <Route path="/requests" element={<RequireCap cap="manageRequests"><RequestsList /></RequireCap>} />
              <Route path="/requests/vm/:id" element={<RequireCap cap="manageRequests"><VmRequestDetail /></RequireCap>} />
              <Route path="/requests/:id" element={<RequireCap cap="manageRequests"><RequestDetail /></RequireCap>} />
              <Route path="/hosts" element={<RequireCap cap="viewAgentList"><Hosts /></RequireCap>} />
              <Route path="/vms" element={<RequireCap cap="viewAgentList"><VmList /></RequireCap>} />
              <Route path="/vms/:id" element={<RequireCap cap="viewAgentList"><VmDetail /></RequireCap>} />
              <Route path="/admin/audit" element={<RequireCap cap="manageEntities"><AuditLog /></RequireCap>} />
              <Route path="/account/notifications" element={<RequireFlag flag="push"><PushSettings /></RequireFlag>} />
              <Route path="/ueber-vitals" element={<AboutVitals />} />
              <Route path="/nutzungsrichtlinien" element={<UsagePolicy />} />
              <Route path="/datenschutz" element={<Privacy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </NavCountsProvider>
          </EntitlementsProvider>
        </MeProvider>
      </RequireEnabled>
    </RequireAuth>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/status/:token" element={<PublicStatus />} />
      <Route path="/pending" element={<PendingActivation />} />
      <Route path="/denied" element={<AccessDenied />} />
      <Route path="/blocked" element={<AccountBlocked />} />
      <Route path="/*" element={<AuthedApp />} />
    </Routes>
  )
}
